import { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";

import useGetImpersonatableUsers from "../../hooks/useGetImpersonatableUsers";
import useImpersonation from "../../hooks/impersonation";

const ViewDashboardAsModal = ({ open, onClose }) => {
  const [search, setSearch] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  const { data: usersByRole, isLoading } = useGetImpersonatableUsers({ enabled: open });
  const { startImpersonation } = useImpersonation();

  const filteredUsersByRole = useMemo(() => {
    if (!usersByRole) return {};
    if (!search.trim()) return usersByRole;

    const term = search.trim().toLowerCase();
    return Object.fromEntries(
      Object.entries(usersByRole).map(([role, users]) => [
        role,
        users.filter(
          (user) =>
            user.full_name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term)
        ),
      ])
    );
  }, [usersByRole, search]);

  const handleClose = () => {
    setSearch("");
    setPendingUser(null);
    onClose();
  };

  const handleConfirm = async () => {
    const user = pendingUser;
    setPendingUser(null);
    await startImpersonation(user);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          View Dashboard As
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            Object.entries(filteredUsersByRole).map(([role, users]) => (
              <Accordion key={role} disableGutters defaultExpanded={Boolean(search.trim())}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {role} ({users.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {users.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                      No users
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {users.map((user) => (
                        <ListItemButton key={user.id} onClick={() => setPendingUser(user)}>
                          <ListItemText primary={user.full_name} secondary={user.email} />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(pendingUser)}
        onClose={() => setPendingUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm View Dashboard As</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to view the dashboard as <b>{pendingUser?.full_name}</b> (
            {pendingUser?.email}) in read-only mode. This session will be logged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setPendingUser(null)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirm}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewDashboardAsModal;
